import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";

const StoreCtx = createContext(null);

export const StoreProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("thiqa_lang") || "ar");
  const [currency, setCurrency] = useState(() => localStorage.getItem("thiqa_currency") || "SAR");
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem("thiqa_cart") || "[]"));
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem("thiqa_user") || "null"));
  const [config, setConfig] = useState(null);

  useEffect(() => {
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
    localStorage.setItem("thiqa_lang", lang);
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("thiqa_currency", currency);
  }, [currency]);

  useEffect(() => {
    localStorage.setItem("thiqa_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const addToCart = useCallback((product, qty = 1) => {
    setCart((c) => {
      const existing = c.find((i) => i.id === product.id);
      if (existing) return c.map((i) => (i.id === product.id ? { ...i, qty: i.qty + qty } : i));
      return [...c, { id: product.id, product, qty }];
    });
  }, []);

  const updateQty = useCallback((id, qty) => {
    setCart((c) => c.map((i) => (i.id === id ? { ...i, qty: Math.max(1, qty) } : i)));
  }, []);

  const removeFromCart = useCallback((id) => {
    setCart((c) => c.filter((i) => i.id !== id));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const login = useCallback((token, u) => {
    localStorage.setItem("thiqa_token", token);
    localStorage.setItem("thiqa_user", JSON.stringify(u));
    setUser(u);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("thiqa_token");
    localStorage.removeItem("thiqa_user");
    setUser(null);
  }, []);

  const formatPrice = useCallback(
    (usd) => {
      if (!config) return `$${usd.toFixed(2)}`;
      const c = config.currencies[currency] || config.currencies.USD;
      const val = usd * c.rate;
      return `${val.toFixed(2)} ${c.symbol}`;
    },
    [config, currency]
  );

  const value = useMemo(
    () => ({
      lang,
      setLang,
      currency,
      setCurrency,
      cart,
      addToCart,
      updateQty,
      removeFromCart,
      clearCart,
      user,
      login,
      logout,
      config,
      formatPrice,
    }),
    [lang, currency, cart, user, config, addToCart, updateQty, removeFromCart, clearCart, login, logout, formatPrice]
  );

  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
};

export const useStore = () => {
  const ctx = useContext(StoreCtx);
  if (!ctx) throw new Error("useStore must be used within StoreProvider");
  return ctx;
};
